import React, { useMemo, useEffect, useState } from 'react';
import { DotsProps } from './DotsProps';
import {
  StyledDot,
  StyledDotContainer,
  StyledDotsContainer,
} from './StyledDots';
import './dots.css';

const Dots = ({
  numDots,
  activeIndex,
  numDotsToShow = 5,
  dotWidth = 30,
  prevNextDotClassName = 'small',
  activeDotClassName = 'active',
}: DotsProps) => {
  const [previousActiveIndex, setPreviousActiveIndex] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [minIndex, setMinIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);

  const dots = useMemo(() => {
    const dots: any = [];
    for (let i = 0; i < numDots; i++) {
      dots.push(
        <StyledDotContainer
          key={i}
          className={i === activeIndex ? 'active' : ''}
        >
          <StyledDot />
        </StyledDotContainer>
      );
    }
    return dots;
  }, [numDots, activeIndex]);

  const [adjustedDots, setAdjustedDots] = useState(dots);

  useEffect(() => {
    const isMovingForward = activeIndex >= previousActiveIndex;
    if (
      (activeIndex > numDotsToShow - 2 && dots.length > numDotsToShow) ||
      hasAnimated
    ) {
      if (isMovingForward) {
        if (activeIndex >= maxIndex && activeIndex !== dots.length - 1) {
          setMinIndex(activeIndex - (numDotsToShow - 2));
          setMaxIndex(activeIndex + 1);
        } else {
          if (minIndex === 0 && maxIndex === 0) {
            if (activeIndex === dots.length - 1) {
              setMaxIndex(activeIndex);
              setMinIndex(activeIndex - (numDotsToShow - 1));
            } else {
              const min = activeIndex - 1 < 0 ? 0 : activeIndex - 1;
              setMinIndex(min);
              setMaxIndex(
                min + (numDotsToShow - 1) > dots.length - 1
                  ? dots.length - 1
                  : min + (numDotsToShow - 1)
              );
            }
          } else {
            if (activeIndex === dots.length - 1) {
              setMaxIndex(dots.length - 1);
              setMinIndex(dots.length - numDotsToShow);
            }
          }
        }
      } else {
        if (activeIndex <= minIndex && activeIndex !== 0) {
          setMinIndex(activeIndex - 1);
          setMaxIndex(activeIndex - 1 + (numDotsToShow - 1));
        } else {
          if (activeIndex === 0) {
            setMaxIndex(numDotsToShow - 1);
            setMinIndex(0);
          }
        }
      }
    } else {
      const lastViewableDotIndex = Math.min(numDotsToShow, dots.length) - 1;

      setMinIndex(0);
      setMaxIndex(lastViewableDotIndex);
    }
  }, [dots, activeIndex, numDotsToShow, hasAnimated]);

  useEffect(() => {
    const isMovingForward = activeIndex >= previousActiveIndex;

    if (
      (activeIndex > numDotsToShow - 2 && dots.length > numDotsToShow) ||
      hasAnimated
    ) {
      setHasAnimated(true);
      const firstViewableDotIndex = minIndex;
      const firstViewableDot = dots[firstViewableDotIndex];
      const lastViewableDotIndex = maxIndex;
      const lastViewableDot = dots[lastViewableDotIndex];

      if (!firstViewableDot || !lastViewableDot) {
        return;
      }

      if (lastViewableDotIndex < adjustedDots.length - 1 && isMovingForward) {
        setAdjustedDots([
          ...dots.slice(0, firstViewableDotIndex),
          React.cloneElement(firstViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(firstViewableDotIndex + 1, lastViewableDotIndex),
          React.cloneElement(lastViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(lastViewableDotIndex + 1),
        ]);
      } else if (lastViewableDotIndex === dots.length - 1) {
        setAdjustedDots([
          ...dots.slice(0, firstViewableDotIndex),
          React.cloneElement(firstViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(firstViewableDotIndex + 1, lastViewableDotIndex),
          activeIndex === lastViewableDotIndex
            ? React.cloneElement(lastViewableDot, {
                className: activeDotClassName,
              })
            : lastViewableDot,
        ]);
      } else if (activeIndex > 1 && !isMovingForward) {
        setAdjustedDots([
          ...dots.slice(0, firstViewableDotIndex),
          React.cloneElement(firstViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(firstViewableDotIndex + 1, lastViewableDotIndex),
          React.cloneElement(lastViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(lastViewableDotIndex + 1),
        ]);
      } else {
        setHasAnimated(false);
        setAdjustedDots([
          ...dots.slice(0, lastViewableDotIndex),
          React.cloneElement(lastViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(lastViewableDotIndex + 1),
        ]);
      }
    } else {
      const lastViewableDotIndex = Math.min(numDotsToShow, dots.length) - 1;

      if (lastViewableDotIndex < dots.length - 1) {
        const lastViewableDot = dots[lastViewableDotIndex];

        setAdjustedDots([
          ...dots.slice(0, lastViewableDotIndex),
          React.cloneElement(lastViewableDot, {
            className: prevNextDotClassName,
          }),
          ...dots.slice(lastViewableDotIndex + 1),
        ]);
      } else {
        setAdjustedDots(dots);
      }
    }
  }, [dots, minIndex, maxIndex, hasAnimated]);

  useEffect(() => {
    setPreviousActiveIndex(activeIndex);
  }, [adjustedDots]);

  const containerWidth =
    dots.length < numDotsToShow
      ? dots.length * dotWidth
      : numDotsToShow * dotWidth;

  const midIndex = Math.floor((minIndex + maxIndex) / 2);

  const leftOffset =
    dots.length < numDotsToShow
      ? 0
      : (dotWidth * numDotsToShow - dotWidth) / 2 - midIndex * dotWidth;

  return (
    <StyledDotsContainer>
      <div
        className="be-carousel-dots-container"
        style={{
          position: 'relative',
          overflow: 'hidden',
          margin: 'auto',
          width: containerWidth + 'px',
        }}
      >
        <ul style={{ transform: `translateX(${leftOffset}px)` }}>
          {' '}
          {adjustedDots}{' '}
        </ul>
      </div>
    </StyledDotsContainer>
  );
};

export default Dots;
